// Mini App "Lifecycle" page (TASK-033). Owns data fetching (TanStack Query
// hook from entities/member) and composes the lifecycle-trend widget.
//
// Per the FSD layer graph, pages may import from widgets, features,
// entities, and shared. They may NOT import from another page.

import { useEffect } from "react";

import {
  CommunitySelector,
  selectActiveCommunityId,
  useActiveCommunityStore,
  useCommunitiesQuery,
} from "@entities/community";
import { useLifecycleQuery } from "@entities/member";
import { LifecycleTrendPanel } from "@widgets/lifecycle-trend";

import { LIFECYCLE_PAGE_STRINGS } from "../lib/strings";

import { LifecycleEmptyState } from "./LifecycleEmptyState";
import { LifecycleErrorState } from "./LifecycleErrorState";
import { LifecycleSkeleton } from "./LifecycleSkeleton";

export function LifecyclePage() {
  const activeCommunityId = useActiveCommunityStore(selectActiveCommunityId);
  const setActiveCommunityId = useActiveCommunityStore(
    (s) => s.setActiveCommunityId,
  );

  const communitiesQuery = useCommunitiesQuery();

  // Auto-select the first community as soon as the list resolves and the
  // store hasn't been seeded yet — same fallback as OverviewPage / ContentPage.
  useEffect(() => {
    if (activeCommunityId !== null) return;
    const first = communitiesQuery.data?.[0];
    if (first) setActiveCommunityId(first.id);
  }, [activeCommunityId, communitiesQuery.data, setActiveCommunityId]);

  const lifecycleQuery = useLifecycleQuery(activeCommunityId);

  const header = (
    <header className="flex items-center justify-between gap-3 pb-2">
      <h1 className="text-lg font-semibold">
        {LIFECYCLE_PAGE_STRINGS.pageTitle}
      </h1>
      <div className="flex items-center gap-2">
        {communitiesQuery.data && communitiesQuery.data.length > 0 ? (
          <CommunitySelector
            communities={communitiesQuery.data}
            activeCommunityId={activeCommunityId}
            onSelect={setActiveCommunityId}
            label="Active community"
          />
        ) : null}
      </div>
    </header>
  );

  // 1) communities list errored — page-wide error state.
  if (communitiesQuery.isError) {
    return (
      <main className="container flex min-h-screen flex-col gap-4 py-4">
        {header}
        <LifecycleErrorState
          title={LIFECYCLE_PAGE_STRINGS.errorTitle}
          body={
            communitiesQuery.error?.message ??
            LIFECYCLE_PAGE_STRINGS.errorBodyDefault
          }
          retryLabel={LIFECYCLE_PAGE_STRINGS.retry}
          onRetry={() => void communitiesQuery.refetch()}
        />
      </main>
    );
  }

  // 2) communities list resolved → user has zero communities → onboarding.
  if (communitiesQuery.data && communitiesQuery.data.length === 0) {
    return (
      <main className="container flex min-h-screen flex-col gap-4 py-4">
        {header}
        <LifecycleEmptyState
          title={LIFECYCLE_PAGE_STRINGS.noCommunity.title}
          body={LIFECYCLE_PAGE_STRINGS.noCommunity.body}
        />
      </main>
    );
  }

  // 3) lifecycle query errored — single retry CTA.
  if (lifecycleQuery.isError) {
    return (
      <main className="container flex min-h-screen flex-col gap-4 py-4">
        {header}
        <LifecycleErrorState
          title={LIFECYCLE_PAGE_STRINGS.errorTitle}
          body={
            lifecycleQuery.error?.message ??
            LIFECYCLE_PAGE_STRINGS.errorBodyDefault
          }
          retryLabel={LIFECYCLE_PAGE_STRINGS.retry}
          onRetry={() => void lifecycleQuery.refetch()}
        />
      </main>
    );
  }

  // 4) Loading — composite skeleton beneath the header so layout doesn't
  // shift when the data lands.
  if (
    !communitiesQuery.data ||
    !lifecycleQuery.data ||
    activeCommunityId === null
  ) {
    return (
      <main className="container flex min-h-screen flex-col gap-4 py-4">
        {header}
        <LifecycleSkeleton loadingLabel={LIFECYCLE_PAGE_STRINGS.loading} />
      </main>
    );
  }

  // 5) Happy path — the panel itself branches on `dataAvailable` and shows
  // either the chart + interpretation note OR the onboarding empty state
  // ("Collecting lifecycle data — X / 30 days collected").
  return (
    <main className="container flex min-h-screen flex-col gap-6 py-4">
      {header}
      <LifecycleTrendPanel response={lifecycleQuery.data} />
    </main>
  );
}
